import {AxiosResponse} from 'axios';
import React from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {isValid as isValidDate} from 'date-fns/isValid';

import {LoadingButton, NewTextInputField} from '@edna/components';
import {delay, storage} from '@edna/utils';

import {Message} from 'src/components';
import {FullForm} from 'src/components/primitives';
import {EStorageKeys} from 'src/constants';
import {showFillDetailsReminder} from 'src/containers/CompanyDetails/thunks';
import {showAcceptTariff, showAgreeTariffChange} from 'src/containers/Tariffs/thunks';
import {ERoles, useLazyGetCurrentUserQuery} from 'src/containers/User';
import {EFixedCacheKey, getErrorCode, getErrorMessage, isErrorWithCode} from 'src/models';
import PATHS from 'src/paths';
import {joiResolver} from 'src/utils/validator';

import CaptchaContainer from '../CaptchaContainer';
import {Layout} from '../Layout';
import {useGetCaptchaQuery, useSignInMutation} from '../api';
import {EAuthErrors, TSignInFormData, TSignInPayload} from '../definitions';
import {calcTimeDifference} from '../utils';
import * as S from './style';

const createSchema: TCreateValidateSchema = (joi) =>
  joi.object({
    username: joi.string().required(),
    password: joi.string().required(),
    captcha: joi.string().required(),
  });

const CAPTCHA_THROTTLE_TIMEOUT = 1000;

const SignIn = React.memo<{onSignInSuccess?: TEmptyFunction}>(({onSignInSuccess}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [signIn, {error, isLoading, reset: resetSignIn}] = useSignInMutation({
    fixedCacheKey: EFixedCacheKey.SIGN_IN,
  });
  const {data: captcha, refetch: refetchCaptcha} = useGetCaptchaQuery();
  const [getCurrentUser] = useLazyGetCurrentUserQuery();

  React.useEffect(() => {
    if (isErrorWithCode(error)) {
      if (getErrorCode(error) === EAuthErrors.TEMPORARY_LOCKED) {
        (async () => {
          await delay(
            calcTimeDifference(Date.now(), (error as AxiosResponse).data?.args?.lockedUntil),
          );
          resetSignIn();
        })();
      }
      refetchCaptcha();
    }
  }, [error, resetSignIn]);

  const initialValues: TSignInFormData = React.useMemo(
    () => ({
      password: '',
      captcha: '',
      username: storage.get(EStorageKeys.USERNAME) ?? '',
    }),
    [],
  );

  const methods = useForm<TSignInPayload>({
    defaultValues: initialValues,
    resolver: joiResolver(createSchema),
  });

  const renderedError = React.useMemo(() => {
    if (!error || !isErrorWithCode(error)) {
      return null;
    }
    let i18nOptions: Record<string, string | number> | undefined;

    if (getErrorCode(error) === EAuthErrors.TEMPORARY_LOCKED) {
      const date = new Date(error?.data.args?.lockedUntil);
      const diff = date.getTime() - Date.now();

      if (isValidDate(date) && diff > 0) {
        i18nOptions = {count: Math.round(diff / 60_000)};
      }
    }

    return (
      <Message
        type="Error"
        title={getErrorMessage(t, error, i18nOptions)}
        data-selector="Auth:error"
      />
    );
  }, [error, t]);

  const isTemporaryLocked = getErrorCode(error) === EAuthErrors.TEMPORARY_LOCKED;

  const handleFormSubmit: SubmitHandler<TSignInPayload> = React.useCallback(
    async (values) => {
      try {
        const result = await signIn({
          ...values,
          captchaId: captcha?.id,
        }).unwrap();

        onSignInSuccess?.();

        if (result.otpId) {
          navigate(PATHS.EMAIL_CONFIRM);

          return;
        }

        const userData = await getCurrentUser().unwrap();

        if (userData.roles?.includes(ERoles.ROLE_USER)) {
          await dispatch(showAgreeTariffChange());
          await dispatch(showFillDetailsReminder());
          await dispatch(showAcceptTariff());
        }
      } catch {}
    },
    [signIn, captcha, dispatch, navigate],
  );

  return (
    <Layout>
      <FormProvider {...methods}>
        <FullForm onSubmit={methods.handleSubmit(handleFormSubmit)}>
          {renderedError}
          <NewTextInputField
            name="username"
            label={t('Auth:signIn.email')}
            autoFocus
            disabled={isTemporaryLocked}
            placeholder="name@company.com"
            data-selector="Auth:inputs.username"
          />
          <NewTextInputField
            type="password"
            name="password"
            label={t('Auth:signIn.password')}
            disabled={isTemporaryLocked}
            data-selector="Auth:inputs.password"
          />
          <CaptchaContainer
            disabled={isTemporaryLocked}
            throttleTimeout={CAPTCHA_THROTTLE_TIMEOUT}
          />
          <S.Actions>
            <S.ActionsRow>
              <LoadingButton
                type="submit"
                appearance="primary"
                disabled={isTemporaryLocked || isLoading}
                loading={isLoading}
                data-selector="Auth:signIn.signInButton"
              >
                {t('Auth:signIn.signInButton')}
              </LoadingButton>
            </S.ActionsRow>
          </S.Actions>
        </FullForm>
      </FormProvider>
    </Layout>
  );
});

SignIn.displayName = 'SignIn';

export {CAPTCHA_THROTTLE_TIMEOUT};
export default SignIn;
